import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { Button } from "react-bootstrap";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import { useDroppable, useDraggable } from "@dnd-kit/core";
import { FaInfoCircle, FaEdit } from "react-icons/fa";
import AddAccountModal from "./AddAccountModal";
import {
  AccountStage,
  AccountStageColors,
  AccountStageOrder,
} from "../../constants/accountStages";
import { formatJoinedDate } from "../../services/CustomerAccountService";
import AccountDetailPanel from "./AccountDetailPanel";
import UserSessionManager from "../../utils/UserSessionManager";
import { useAccountsDashboard } from "./AccountsPageLayout";

const COLUMN_ORDER = AccountStageOrder;

const DashboardContainer = styled.div`
  display: flex;
  gap: 16px;
  padding: 0 0 24px;
  background: #f5f6fa;
  min-height: 60vh;
  font-family: "Inria Serif", Georgia, serif;
`;

const ColumnBase = styled.div`
  background: #fff;
  flex: 1;
  border-radius: 8px;
  padding: 12px;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
`;

const AccountCardBase = styled.div`
  background: ${({ $tint }) => $tint || "#fff"};
  border-radius: 8px;
  border: 1px solid #e9ecef;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  margin-bottom: 10px;
  padding: 10px 12px 10px;
  font-family: "Inria Serif", Georgia, serif;
  color: #212529;
`;

const ColumnTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 0 10px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid #e0e0e0;
  color: #333;
  font-weight: 700;
  font-size: 0.85rem;
  letter-spacing: 0.04em;
`;

const BadgeCount = styled.span`
  background: #e9ecef;
  color: #495057;
  font-size: 0.7rem;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 600;
`;

const NameRow = styled.div`
  font-weight: 700;
  font-size: 0.95rem;
  margin-bottom: 8px;
  color: #1a1a1a;
`;

const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  margin-bottom: 4px;
  color: #495057;
`;

const MetaLabel = styled.span`
  color: #6c757d;
`;

const CardList = styled.div`
  min-height: 120px;
`;

const IconActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  padding: 0;
  font-size: 0.8rem;
  color: #fff;
  background: ${({ $variant }) => ($variant === "edit" ? "#5a6268" : "#0d6efd")};
  cursor: pointer;
  flex-shrink: 0;
  line-height: 1;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }

  &:hover:not(:disabled) {
    filter: brightness(0.9);
  }
`;

function groupByStage(list) {
  const grouped = {};
  COLUMN_ORDER.forEach((s) => {
    grouped[s] = [];
  });
  list.forEach((acc) => {
    const col = COLUMN_ORDER.includes(acc.stage) ? acc.stage : AccountStage.NEW_ACCOUNT;
    grouped[col].push(acc);
  });
  return grouped;
}

function DroppableColumn({ status, count, children }) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  return (
    <ColumnBase
      ref={setNodeRef}
      style={{
        outline: isOver ? "2px dashed #0d6efd" : undefined,
        outlineOffset: 2,
      }}
    >
      <ColumnTitle>
        {status.toUpperCase()}
        <BadgeCount>{count}</BadgeCount>
      </ColumnTitle>
      <CardList>{children}</CardList>
    </ColumnBase>
  );
}

function DraggableAccountCard({ account, columnStage, onInfo, onEdit }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: account.accountId,
  });
  const tint = AccountStageColors[columnStage] || "#fff";
  const isChurned = account.stage === AccountStage.CHURNED;

  return (
    <AccountCardBase
      ref={setNodeRef}
      $tint={tint}
      data-testid={`account-card-${account.accountId}`}
      style={{
        opacity: isDragging ? 0.55 : 1,
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
      }}
    >
      <div className="d-flex align-items-start justify-content-between gap-1">
        <div
          className="flex-grow-1 min-w-0"
          style={{ cursor: "grab" }}
          {...attributes}
          {...listeners}
        >
          <NameRow className="text-truncate">{account.company}</NameRow>
          <MetaRow>
            <MetaLabel>Contact</MetaLabel>
            <span className="text-end text-truncate ms-1" style={{ maxWidth: "58%" }}>
              {account.contactName || "—"}
            </span>
          </MetaRow>
          <MetaRow>
            <MetaLabel>Cash Inflow</MetaLabel>
            <span>{account.cashInflow || account.revenue || "—"}</span>
          </MetaRow>
          <MetaRow>
            <MetaLabel>Joined</MetaLabel>
            <span>{formatJoinedDate(account.joined)}</span>
          </MetaRow>
        </div>
        <div className="d-flex flex-shrink-0 gap-2">
          <IconActionBtn
            type="button"
            $variant="info"
            title="Edit account"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(account);
            }}
          >
            <FaInfoCircle />
          </IconActionBtn>
          <IconActionBtn
            type="button"
            $variant="edit"
            title={isChurned ? "Unavailable for churned accounts" : "Account details"}
            disabled={isChurned}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              if (!isChurned) onInfo(account);
            }}
          >
            <FaEdit />
          </IconActionBtn>
        </div>
      </div>
    </AccountCardBase>
  );
}

const AccountsWidgets = () => {
  const {
    accounts,
    loading,
    error,
    reload,
    upsertAccount,
    patchAccountStage,
  } = useAccountsDashboard();

  const [activeAccount, setActiveAccount] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editAccount, setEditAccount] = useState(null);
  const [dragAccount, setDragAccount] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const userInfo = UserSessionManager.getUserInfo();
  const grouped = useMemo(() => groupByStage(accounts), [accounts]);

  const findIndex = (id) =>
    accounts.findIndex((a) => String(a.accountId) === String(id));

  const handleDragStart = (event) => {
    const acc = accounts[findIndex(event.active.id)];
    setDragAccount(acc || null);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setDragAccount(null);
    if (!over) return;
    const destStage = over.id;
    const idx = findIndex(active.id);
    if (idx === -1) return;
    const acc = accounts[idx];
    if (!acc || acc.stage === destStage) return;

    try {
      await patchAccountStage(acc.accountId, destStage);
    } catch {
      /* state reverted in patchAccountStage */
    }
  };

  const openPanel = (acc) => {
    setActiveAccount(acc);
    setPanelOpen(true);
  };

  const closePanel = () => {
    setPanelOpen(false);
    setActiveAccount(null);
  };

  const openEdit = (acc) => {
    setEditAccount(acc);
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditAccount(null);
  };

  const handlePanelStageChange = async (accountId, stage) => {
    try {
      await patchAccountStage(accountId, stage);
      setActiveAccount((prev) =>
        prev && String(prev.accountId) === String(accountId) ? { ...prev, stage } : prev,
      );
    } catch {
      /* reverted in context */
    }
  };

  const handlePanelAccountUpdated = (patch) => {
    if (!patch?.accountId) return;
    upsertAccount(patch);
    setActiveAccount((prev) =>
      prev && String(prev.accountId) === String(patch.accountId)
        ? { ...prev, ...patch }
        : prev,
    );
  };

  const handleAccountCreate = (account) => {
    upsertAccount(account);
    setAddOpen(false);
    reload();
  };

  const handleAccountUpdate = (account) => {
    upsertAccount(account);
    setActiveAccount((prev) =>
      prev && String(prev.accountId) === String(account.accountId)
        ? { ...prev, ...account }
        : prev,
    );
    closeEdit();
    reload();
  };

  if (loading) {
    return (
      <div className="py-5 text-center text-muted" style={{ fontFamily: "Georgia, serif" }}>
        Loading accounts…
      </div>
    );
  }

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
        <Button variant="dark" size="sm" className="rounded-1 px-3" onClick={() => setAddOpen(true)}>
          + Add Account
        </Button>      
      </div>

      {error ? (
        <div className="alert alert-danger small mb-3 py-2">{error}</div>
      ) : null}

      {!error && accounts.length === 0 ? (
        <div className="text-center text-muted py-5">No accounts yet. Create your first account.</div>
      ) : null}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <DashboardContainer>
          {COLUMN_ORDER.map((status) => (
            <DroppableColumn status={status} key={status} count={grouped[status].length}>
              {grouped[status].map((acc) => (
                <DraggableAccountCard
                  key={acc.accountId}
                  account={acc}
                  columnStage={status}
                  onInfo={openPanel}
                  onEdit={openEdit}
                />
              ))}
            </DroppableColumn>
          ))}
        </DashboardContainer>
        <DragOverlay dropAnimation={null}>
          {dragAccount ? (
            <AccountCardBase $tint={AccountStageColors[dragAccount.stage]}>
              <NameRow>{dragAccount.company}</NameRow>
            </AccountCardBase>
          ) : null}
        </DragOverlay>
      </DndContext>

      <AccountDetailPanel
        open={panelOpen}
        account={activeAccount}
        onClose={closePanel}
        onStageChange={handlePanelStageChange}
        onAccountUpdated={handlePanelAccountUpdated}
      />

      <AddAccountModal
        show={addOpen}
        onHide={() => setAddOpen(false)}
        onCreate={handleAccountCreate}
      />
      <AddAccountModal
        show={editOpen}
        onHide={closeEdit}
        account={editAccount}
        onUpdate={handleAccountUpdate}
      />
    </>
  );
};

export default AccountsWidgets;
